import { BillType } from '@/model/Bill'
import { Role } from '@/model/Employee'
import { getGuideGroupForSlug } from '@/documentation/guideGroups'
import { authService } from '@/services/AuthService'
import { useProfileStore } from '@/store/profile.module'
import HomeView from '@/views/HomeView.vue'
import { createRouter, createWebHistory } from 'vue-router'

const STRIPE_SUBSCRIPTION_ROUTE_NAMES = new Set([
  'accountSubscription',
  'billingConfig',
  'subscriptionConfig',
  'stripeSubscription'
])

const DELIVERY_NOTE_PARSER_ROUTE_NAMES = new Set(['parserEmailConfig', 'deliveryNoteParser'])

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/CustomersView.vue')
    },
    {
      path: '/customers/import',
      name: 'customerImport',
      component: () => import('../views/CustomerImportView.vue')
    },
    {
      path: '/customers/:customerId',
      name: 'customerDetail',
      component: () => import('../views/CustomerDetailView.vue')
    },
    {
      path: '/vehicles',
      name: 'vehicles',
      component: () => import('../views/VehiclesView.vue')
    },
    {
      path: '/vehicles/import',
      name: 'vehicleImport',
      component: () => import('../views/VehicleImportView.vue')
    },
    {
      path: '/vehicles/:vehicleId',
      name: 'vehicleDetail',
      component: () => import('../views/VehicleDetailView.vue')
    },
    {
      path: '/places',
      name: 'places',
      component: () => import('../views/PlacesView.vue')
    },
    {
      path: '/places/import',
      name: 'placeImport',
      component: () => import('../views/PlaceImportView.vue')
    },
    {
      path: '/places/:placeId',
      name: 'placeDetail',
      component: () => import('../views/PlaceDetailView.vue')
    },
    {
      path: '/payloads',
      name: 'payloads',
      component: () => import('../views/PayloadsView.vue')
    },
    {
      path: '/payloads/import',
      name: 'payloadImport',
      component: () => import('../views/PayloadImportView.vue')
    },
    {
      path: '/payloads/:payloadId',
      name: 'payloadDetail',
      component: () => import('../views/PayloadDetailView.vue')
    },
    {
      path: '/prices',
      name: 'prices',
      component: () => import('../views/PricesView.vue')
    },
    {
      path: '/prices/:priceId',
      name: 'priceDetail',
      component: () => import('../views/PriceDetailView.vue')
    },
    {
      path: '/employees',
      name: 'employees',
      component: () => import('../views/EmployeesView.vue')
    },
    {
      path: '/employees/:employeeId',
      name: 'employeeDetail',
      component: () => import('../views/EmployeeDetailView.vue')
    },
    {
      path: '/drivers',
      name: 'drivers',
      component: () => import('../views/DriversView.vue')
    },
    {
      path: '/drivers/:driverId',
      name: 'driverDetail',
      component: () => import('../views/DriverDetailView.vue')
    },
    {
      path: '/employee-documents',
      name: 'employeeDocuments',
      component: () => import('../views/EmployeeDocumentsView.vue')
    },
    {
      path: '/employee-documents/:id',
      name: 'employeeDocumentDetail',
      component: () => import('../views/EmployeeDocumentDetailView.vue')
    },
    {
      path: '/vehicle-documents',
      name: 'vehicleDocuments',
      component: () => import('../views/VehicleDocumentsView.vue')
    },
    {
      path: '/vehicle-documents/:id',
      name: 'vehicleDocumentDetail',
      component: () => import('../views/VehicleDocumentDetailView.vue')
    },
    {
      path: '/refuels',
      name: 'refuels',
      component: () => import('../views/RefuelsView.vue')
    },
    {
      path: '/refuels/:refuelId',
      name: 'refuelDetail',
      component: () => import('../views/RefuelDetailView.vue')
    },
    {
      path: '/deliveryNotes',
      name: 'deliveryNotes',
      component: () => import('@/views/DeliveryNotesView.vue')
    },
    {
      path: '/passengerTransportDocuments',
      name: 'passengerTransportDocuments',
      component: () => import('@/views/PassengerTransportDocumentsView.vue')
    },
    {
      path: '/passengerTransportDocuments/:passengerTransportDocumentId',
      name: 'passengerTransportDocumentDetail',
      component: () => import('@/views/PassengerTransportDocumentDetailView.vue')
    },
    {
      path: '/loadOrders',
      name: 'loadOrders',
      component: () => import('@/views/LoadOrdersView.vue')
    },
    {
      path: '/loadOrders/:loadOrderId',
      name: 'loadOrderDetail',
      component: () => import('@/views/LoadOrderDetailView.vue')
    },
    {
      path: '/deliveryNotes/:deliveryNoteId',
      name: 'deliveryNoteDetail',
      component: () => import('../views/DeliveryNoteDetailView.vue')
    },
    {
      path: '/delivery-note-parser',
      name: 'deliveryNoteParser',
      component: () => import('../views/DeliveryNoteParserView.vue')
    },
    {
      path: '/tachograph-parser',
      name: 'tachographParser',
      component: () => import('../views/TachographParserView.vue')
    },
    {
      path: '/bills',
      name: 'bills',
      component: () => import('../views/BillsView.vue')
    },
    {
      path: '/bills/:billId',
      name: 'billDetail',
      component: () => import('../views/BillDetailView.vue')
    },
    {
      path: '/bills/:billId/edit',
      name: 'billEdit',
      component: () => import('../views/BillEditView.vue')
    },
    {
      path: '/newBill',
      name: 'newBill',
      component: () => import('@/views/BillTypeSelectionView.vue')
    },
    {
      path: '/newBill/standard',
      name: 'newStandardBill',
      component: () => import('@/views/NewBillView.vue'),
      props: { billType: BillType.STANDARD }
    },
    {
      path: '/newBill/simplified',
      name: 'newSimplifiedBill',
      component: () => import('@/views/NewBillView.vue'),
      props: { billType: BillType.SIMPLIFIED }
    },
    {
      path: '/newBill/corrective',
      name: 'newCorrectiveBill',
      component: () => import('@/views/NewCorrectiveBillView.vue'),
      props: { billType: BillType.CORRECTIVE }
    },
    {
      path: '/newBill/simplified-corrective',
      name: 'newSimplifiedCorrectiveBill',
      component: () => import('@/views/NewCorrectiveBillView.vue'),
      props: { billType: BillType.SIMPLIFIED_CORRECTIVE }
    },
    {
      path: '/expenses',
      name: 'expenses',
      component: () => import('../views/ExpensesView.vue')
    },
    {
      path: '/expenses/:expenseId',
      name: 'expenseDetail',
      component: () => import('../views/ExpenseDetailView.vue')
    },
    {
      path: '/account',
      component: () => import('@/views/AccountView.vue'),
      children: [
        {
          path: '',
          name: 'account',
          redirect: { name: 'accountProfile' }
        },
        {
          path: 'profile',
          name: 'accountProfile',
          component: () => import('@/views/CompanyProfileView.vue')
        },
        {
          path: 'subscription',
          name: 'accountSubscription',
          component: () => import('@/views/BillingConfigView.vue')
        }
      ]
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      children: [
        {
          path: 'deliveryNotesConfig',
          name: 'deliveryNotesConfig',
          component: () => import('@/views/DeliveryNotesConfigView.vue')
        },
        {
          path: 'passengerTransportDocumentsConfig',
          name: 'passengerTransportDocumentsConfig',
          component: () => import('@/views/PassengerTransportDocumentsConfigView.vue')
        },
        {
          path: 'loadOrdersConfig',
          name: 'loadOrdersConfig',
          component: () => import('@/views/LoadOrdersConfigView.vue')
        },
        {
          path: 'billsConfig',
          name: 'billsConfig',
          component: () => import('@/views/BillsConfigView.vue')
        },
        {
          path: 'certificateConfig',
          name: 'certificateConfig',
          component: () => import('@/views/CertificateConfigView.vue')
        },
        {
          path: 'parserEmailConfig',
          name: 'parserEmailConfig',
          component: () => import('@/views/ParserEmailConfigView.vue')
        },
        {
          path: 'billingConfig',
          name: 'billingConfig',
          redirect: { name: 'accountSubscription' }
        },
        {
          path: 'subscriptionConfig',
          name: 'subscriptionConfig',
          redirect: { name: 'accountSubscription' }
        },
        {
          path: 'stripeSubscription',
          name: 'stripeSubscription',
          redirect: { name: 'accountSubscription' }
        },
        {
          path: 'nimas',
          name: 'nimas',
          component: () => import('../views/NimasView.vue')
        },
        {
          path: 'developers',
          name: 'developers',
          component: () => import('@/views/DevelopersView.vue')
        },
      ]
    },
    {
      path: '/news',
      name: 'news',
      component: () => import('../views/NewsView.vue')
    },
    {
      path: '/news/:id',
      name: 'newsDetail',
      component: () => import('../views/NewsDetailView.vue')
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue')
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue')
    },
    {
      path: '/register-company',
      name: 'register-company',
      component: () => import('../views/RegisterCompanyView.vue')
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: () => import('../views/TermsAndConditionsView.vue')
    },
    {
      path: '/sharedLink',
      name: 'sharedLink',
      component: () => import('../views/SharedLinkView.vue')
    },
    {
      path: '/share/:shareId/edit',
      name: 'deliveryNoteSharePublicEdit',
      component: () => import('../views/DeliveryNoteShareEditView.vue')
    },
    {
      path: '/share/:shareId',
      name: 'deliveryNoteSharePublic',
      component: () => import('../views/DeliveryNoteShareView.vue')
    },
    {
      path: '/ptd-share/:shareId',
      name: 'passengerTransportDocumentSharePublic',
      component: () => import('../views/PassengerTransportDocumentShareView.vue')
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('../views/UnauthorizedView.vue')
    },
    {
      path: '/account-blocked',
      name: 'account-blocked',
      component: () => import('../views/BlockedAccountView.vue')
    },
    {
      path: '/production',
      name: 'production',
      component: () => import('@/views/ProductionDashboardView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/billing-dashboard',
      name: 'billingDashboard',
      component: () => import('@/views/BillingDashboardView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/general-dashboard',
      name: 'companyDashboard',
      component: () => import('@/views/CompanyDashboardView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/design-system',
      name: 'designSystem',
      component: () => import('@/views/DesignSystemView.vue')
    },
    {
      path: '/api-docs',
      component: () => import('@/views/api-docs/ApiDocsView.vue'),
      children: [
        {
          path: '',
          name: 'apiDocs',
          component: () => import('@/views/documentation/developers/DevelopersTransportDocumentsView.vue')
        },
        {
          path: 'transport-documents',
          redirect: '/api-docs'
        }
      ]
    },
    {
      path: '/guides',
      component: () => import('@/views/guides/GuidesView.vue'),
      children: [
        {
          path: '',
          name: 'guidesHome',
          component: () => import('@/views/guides/GuidesHomeView.vue')
        },
        {
          path: ':groupId',
          name: 'guidesGroup',
          component: () => import('@/views/guides/GuidesGroupView.vue')
        }
      ]
    },
    {
      path: '/documentation',
      redirect: '/guides'
    },
    {
      path: '/documentation/tutorial/:slug',
      redirect: (to) => {
        const slug = to.params.slug as string
        const group = getGuideGroupForSlug(slug)
        if (group) {
          return `/guides/${group.id}#${slug}`
        }
        return '/guides'
      }
    },
    {
      path: '/documentation/developers/transport-documents',
      redirect: '/api-docs'
    },
    {
      path: '/documentation/developers/getting-started',
      redirect: '/api-docs#api-introduction'
    },
    {
      path: '/documentation/developers/api-keys',
      redirect: '/api-docs#configure-api-key'
    },
    {
      path: '/documentation/developers/:slug',
      redirect: '/api-docs'
    }
  ]
})

router.beforeResolve(async (to) => {
  const storeProfile = useProfileStore()

  if (to.name === 'terms-and-conditions') {
    // If the user is trying to access the terms and conditions page, allow it
    return true
  }

  const authenticated = await authService.isAuthenticated()

  // make sure the user is authenticated
  // ❗️ Avoid an infinite redirect
  if (!authenticated
    && to.name !== 'login'
    && to.name !== 'register'
    && to.name !== 'sharedLink'
    && to.name !== 'deliveryNoteSharePublic'
    && to.name !== 'deliveryNoteSharePublicEdit'
    && to.name !== 'passengerTransportDocumentSharePublic') {
    // redirect the user to the login page
    return 'login'
  }

  if (authenticated) {
    await storeProfile.ensureProfileLoaded()

    if (storeProfile.selectedCompany === undefined || storeProfile.selectedCompany.role === undefined) {
      if (to.name === 'deliveryNoteSharePublic'
        || to.name === 'deliveryNoteSharePublicEdit'
        || to.name === 'passengerTransportDocumentSharePublic'
        || to.name === 'sharedLink') {
        return true
      }
      if (to.name !== 'register-company') {
        // If the user is authenticated but the selected company doesn't exist, redirect to register company
        return '/register-company'
      }
    } else {
      const isPublicShareRoute = to.name === 'deliveryNoteSharePublic'
        || to.name === 'deliveryNoteSharePublicEdit'
        || to.name === 'passengerTransportDocumentSharePublic'
        || to.name === 'sharedLink'

      if (storeProfile.selectedCompany.role !== Role.ADMIN &&
        to.name !== 'unauthorized' &&
        !isPublicShareRoute) {
        // Unauthorized user
        return '/unauthorized'
      }

      if (storeProfile.selectedCompany.role === Role.ADMIN) {
        await storeProfile.ensureSelectedCompanyRecordLoaded()
        const isAccountBlocked = storeProfile.isSelectedCompanyAccessBlocked

        if (isAccountBlocked && to.name !== 'account-blocked' && !isPublicShareRoute) {
          return '/account-blocked'
        }

        if (!isAccountBlocked && to.name === 'account-blocked') {
          return '/'
        }

        if (to.name === 'login' || to.name === 'unauthorized' || to.name === 'register' || to.name === 'register-company') {
          return isAccountBlocked ? '/account-blocked' : '/'
        }
      }

      const companyId = storeProfile.selectedCompany?.id
      if (companyId && STRIPE_SUBSCRIPTION_ROUTE_NAMES.has(String(to.name))) {
        await Promise.all([
          storeProfile.loadCompanyFeatureFlags(companyId),
          storeProfile.loadGlobalFeatureFlags()
        ])
        if (!storeProfile.isStripeBillingEnabled(companyId)) {
          return { name: 'accountProfile' }
        }
      }

      if (companyId && DELIVERY_NOTE_PARSER_ROUTE_NAMES.has(String(to.name))) {
        await storeProfile.loadCompanyFeatureFlags(companyId)
        const companyFlags = storeProfile.getCompanyFeatureFlags(companyId)
        if (!companyFlags?.showDeliveryNoteParserInWeb) {
          if (to.name === 'parserEmailConfig') {
            return { name: 'deliveryNotesConfig' }
          }
          return { name: 'home' }
        }
      }
    }


  }

})

export default router
